import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, CreditCard, RefreshCw, Sparkles, LogOut, Send, Clock } from "lucide-react";
import { API_BASE } from "@/lib/api";

const TOKEN_KEY = "nv_admin_jwt";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [drips, setDrips] = useState([]);
  const [stats, setStats] = useState(null);

  const authedFetch = (path) =>
    fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });

  const loadData = async (activeToken) => {
    try {
      const [leadsRes, statsRes, dripsRes] = await Promise.all([
        fetch(`${API_BASE}/admin/leads`, { headers: { Authorization: `Bearer ${activeToken}` } }),
        fetch(`${API_BASE}/admin/stats`, { headers: { Authorization: `Bearer ${activeToken}` } }),
        fetch(`${API_BASE}/admin/drips?limit=200`, { headers: { Authorization: `Bearer ${activeToken}` } }),
      ]);
      if (!leadsRes.ok || !statsRes.ok || !dripsRes.ok) throw new Error("Unauthorized");
      setLeads(await leadsRes.json());
      setStats(await statsRes.json());
      setDrips(await dripsRes.json());
    } catch {
      toast.error("Session expired. Please sign in again.");
      signOut();
    }
  };

  // On mount: if we have a token, verify with /auth/me
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved) { setChecking(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${saved}` } });
        if (!res.ok) throw new Error("bad token");
        const me = await res.json();
        setUser(me);
        setToken(saved);
        await loadData(saved);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async () => {
    if (!email || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatDetail(data.detail));
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      setPassword("");
      await loadData(data.access_token);
      toast.success(`Welcome, ${data.user.name || data.user.email}`);
    } catch (e) {
      toast.error(e.message || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    setLeads([]);
    setDrips([]);
    setStats(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="text-[#4A4A4A]">Checking session…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#183D36] text-sm mb-6">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div className="rounded-3xl bg-white border border-[#E5E0D8] p-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center">
                <Sparkles size={16} />
              </span>
              <span className="font-heading text-lg font-semibold text-[#183D36]">NextVisit</span>
            </div>
            <div className="overline mb-3">Admin sign-in</div>
            <h1 className="font-heading text-3xl text-[#183D36] mb-6">Welcome back.</h1>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="brooke@bookyournextvisit.com"
                  className="mt-2"
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && signIn()}
                  placeholder="••••••••"
                  className="mt-2"
                  autoComplete="current-password"
                />
              </div>
              <Button
                data-testid="admin-signin-btn"
                onClick={signIn}
                disabled={loading}
                className="w-full rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] py-6 mt-2"
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <div className="text-center pt-1">
                <a href="/forgot-password" className="text-xs text-[#4A4A4A] hover:text-[#183D36] underline" data-testid="admin-forgot-link">
                  Forgot your password?
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <div className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm text-[#4A4A4A] hover:text-[#183D36] flex items-center gap-2">
              <ArrowLeft size={14} /> Site
            </Link>
            <h1 className="font-heading text-lg text-[#183D36]">NextVisit — Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/studio" data-testid="admin-studio-link" className="text-sm text-[#183D36] hover:text-[#F4B183] font-medium">Studio →</Link>
            <span className="hidden sm:inline text-sm text-[#4A4A4A]" data-testid="admin-user-email">{user.email}</span>
            <Button variant="ghost" size="sm" data-testid="admin-refresh-btn" onClick={() => loadData(token)} className="rounded-full">
              <RefreshCw size={14} className="mr-2" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" data-testid="admin-signout-btn" onClick={signOut} className="rounded-full">
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label="Trial signups" value={stats?.total_leads ?? "—"} testid="admin-stat-leads" />
          <StatCard icon={CreditCard} label="Paid subscriptions" value={stats?.total_paid ?? "—"} testid="admin-stat-paid" />
          <StatCard icon={Clock} label="Drips scheduled" value={stats?.pending_drips ?? "—"} testid="admin-stat-pending-drips" />
          <StatCard icon={Send} label="Drips sent" value={stats?.sent_drips ?? "—"} testid="admin-stat-sent-drips" />
        </div>

        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="mb-6 rounded-full bg-white border border-[#E5E0D8] p-1">
            <TabsTrigger data-testid="admin-tab-leads" value="leads" className="rounded-full data-[state=active]:bg-[#183D36] data-[state=active]:text-[#FAF7F0]">Leads</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-drips" value="drips" className="rounded-full data-[state=active]:bg-[#183D36] data-[state=active]:text-[#FAF7F0]">Drip queue</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <div className="rounded-3xl bg-white border border-[#E5E0D8] overflow-hidden">
              <div className="p-6 border-b border-[#E5E0D8]">
                <h2 className="font-heading text-xl text-[#183D36]">Recent leads</h2>
              </div>
              <div className="overflow-x-auto">
                <Table data-testid="admin-leads-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Business</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Signed up</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-[#4A4A4A] py-10">No leads yet.</TableCell></TableRow>
                    ) : leads.map((l) => (
                      <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`}>
                        <TableCell className="font-medium text-[#183D36]">{l.business_name}</TableCell>
                        <TableCell>{l.contact_name}</TableCell>
                        <TableCell>
                          {l.email}
                          {l.unsubscribed && (
                            <span
                              data-testid={`admin-lead-unsub-${l.id}`}
                              className="ml-2 inline-block px-2 py-0.5 rounded-full bg-[#F4B183] text-[#183D36] text-[10px] font-accent uppercase tracking-widest font-semibold"
                            >
                              unsub
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{l.business_type || "—"}</TableCell>
                        <TableCell>{l.phone || "—"}</TableCell>
                        <TableCell className="text-sm text-[#4A4A4A]">{new Date(l.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="drips">
            <div className="rounded-3xl bg-white border border-[#E5E0D8] overflow-hidden">
              <div className="p-6 border-b border-[#E5E0D8] flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-heading text-xl text-[#183D36]">Drip queue</h2>
                  <p className="text-sm text-[#4A4A4A] mt-1">Trial nurture emails scheduled to send at day 2 · 5 · 10 · 12 · 14.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-accent uppercase tracking-widest text-[#F4B183]" data-testid="admin-drips-count">
                    {drips.filter(d => d.status === "pending").length} pending · {drips.length} total
                  </span>
                  <Button
                    data-testid="admin-purge-test-btn"
                    variant="ghost"
                    size="sm"
                    className="rounded-full border border-[#183D36]/20 text-[#183D36]"
                    onClick={async () => {
                      if (!window.confirm("Cancel all pending drips that look like test data (example.com, TEST_, sandbox+, delivered@resend.dev)?")) return;
                      try {
                        const r = await fetch(`${API_BASE}/admin/drips/purge-test`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        const data = await r.json();
                        if (!r.ok) throw new Error(data.detail || "Failed");
                        toast.success(`Cancelled ${data.cancelled} test drip(s)`);
                        loadData(token);
                      } catch (e) {
                        toast.error(String(e.message || e));
                      }
                    }}
                  >
                    Purge test data
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table data-testid="admin-drips-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kind</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Send at</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drips.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-[#4A4A4A] py-10">No drips scheduled yet.</TableCell></TableRow>
                    ) : drips.map((d) => (
                      <TableRow key={d.id} data-testid={`admin-drip-row-${d.id}`}>
                        <TableCell className="font-medium text-[#183D36] capitalize">{d.kind.replace('_', ' ')}</TableCell>
                        <TableCell>{d.email}</TableCell>
                        <TableCell>{d.business_name}</TableCell>
                        <TableCell className="text-sm text-[#4A4A4A]">{new Date(d.send_at).toLocaleString()}</TableCell>
                        <TableCell><DripBadge status={d.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5E0D8] p-6" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-2xl bg-[#A8BFA3]/40 flex items-center justify-center">
          <Icon size={16} className="text-[#183D36]" />
        </span>
        <span className="text-sm text-[#4A4A4A]">{label}</span>
      </div>
      <div className="font-heading text-3xl text-[#183D36] mt-3">{value}</div>
    </div>
  );
}

function DripBadge({ status }) {
  const styles = {
    pending: "bg-[#EFECE5] text-[#252525]",
    sending: "bg-[#F4B183] text-[#183D36]",
    sent: "bg-[#A8BFA3] text-[#183D36]",
    failed: "bg-red-100 text-red-700",
    cancelled: "bg-[#EFECE5] text-[#4A4A4A]",
  }[status] || "bg-[#EFECE5] text-[#252525]";
  return (
    <Badge className={`${styles} rounded-full font-accent uppercase tracking-widest text-[10px] font-semibold`} data-testid={`drip-status-${status}`}>{status}</Badge>
  );
}
